import { useRef } from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

let globalId = 0;
const genId = () => ++globalId;

export function useId(prefix?: string): string {
  const idRef = useRef<string>();
  if (idRef.current === undefined) {
    idRef.current = `${prefix || 'id'}-${genId()}`;
  }

  return idRef.current;
}

export function isAnswerUncertain(response: string): { uncertain: boolean; language: string | null } {
  // Define uncertainty patterns for various languages
  const uncertaintyPatternsByLanguage: Record<string, RegExp[]> = {
    en: [
      /i(?:\s|['\u2018\u2019])?m(?:\s+really)?\s+not\s+sure/i,
      /i(?:\s+am)?(?:\s+really)?\s+not\s+sure/i,
      /i\s+(?:really\s+)?don(?:['\u2018\u2019])?t\s+know/i,
      /it(?:['\u2018\u2019]s| is) unclear/i,
      /cannot determine/i,
      /hard to say/i,
      /difficult to determine/i,
    ],
    es: [/no (?:lo )?sé/i, /no estoy seguro/i, /no es claro/i, /desconocido/i],
    fr: [/je ne sais pas/i, /je ne suis pas sûr/i, /ce n'est pas clair/i, /inconnu/i],
    de: [/ich weiß nicht/i, /ich bin nicht sicher/i, /es ist unklar/i, /unbekannt/i],
    it: [/non lo so/i, /non sono sicuro/i, /non è chiaro/i, /sconosciuto/i],
    pt: [/não sei/i, /não tenho certeza/i, /não está claro/i, /desconhecido/i],
    nl: [/ik weet het niet/i, /ik weet niet zeker/i, /het is onduidelijk/i, /onbekend/i],
    pl: [/nie wiem/i, /nie jestem pewien/i, /to nie jest jasne/i, /nieznany/i],
    ru: [/я не знаю/i, /я не уверен/i, /это не ясно/i, /неизвестно/i],
    ja: [/知りません/i, /よくわからない/i, /不明です/i, /未知/i],
    ko: [/모릅니다/i, /잘 모르겠습니다/i, /불확실합니다/i, /알 수 없음/i],
    zh: [/我不知道/i, /我不确定/i, /不清楚/i, /未知/i],
    'zh-TW': [/我不知道/i, /我不確定/i, /不清楚/i, /未知/i],
  };

  let earliestMatch: { index: number; language: string } | null = null;

  // Find the earliest match across all languages
  for (const [language, patterns] of Object.entries(uncertaintyPatternsByLanguage)) {
    for (const pattern of patterns) {
      const match = response.match(pattern);
      if (match && (earliestMatch === null || match.index! < earliestMatch.index)) {
        earliestMatch = { index: match.index!, language };
      }
    }
  }

  return earliestMatch ? { uncertain: true, language: earliestMatch.language } : { uncertain: false, language: null };
}
